import {useState} from 'react'
import dayjs from '@/dayjs'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Download01Icon, LayersThree01Icon} from '@/components/ui/icon/Icon'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {ExportJob} from '@/features/exports/types'
import {useExportsIndicatorStore} from '@/features/exports/hooks/useExportsIndicator'
import {useExportAnnouncementStore} from '@/features/exports/hooks/useExportAnnouncement'
import {useGetMergedReportAvailableMonths} from '@/features/accesses/activity-report/queries/useGetMergedReportAvailableMonths'
import {useExportMergedReportByMonth} from '@/features/accesses/activity-report/queries/useExportMergedReportByMonth'
import {
    formatMonthPeriodLabel,
    periodToMonthYear,
    resolveActivityReportErrorLabel
} from '@/features/accesses/activity-report/utils'
import {ExportCardShell} from './ExportCardShell'
import {StyledCardFooter, StyledIconChip, StyledSelectWrapper} from './style'

// Operational card that enqueues the unified CSV (domiciliary + outpatient of the same month) as an
// async export job and follows it through the shared exports machinery (toggle dot + popover +
// history). The backend availability list only offers months where BOTH sector reports are ready.
export const MergeReportsCard = () => {
    const {t} = useTranslation()
    const {data, isPending: isLoadingMonths} = useGetMergedReportAvailableMonths()
    const months = data?.months ?? []
    const hasMonths = months.length > 0

    const [selectedMonthChoice, setSelectedMonthChoice] = useState<string>()
    const selectedMonth = selectedMonthChoice && months.includes(selectedMonthChoice) ? selectedMonthChoice : months[0]

    const exportMutation = useExportMergedReportByMonth({
        onSuccess: ({exportJob: job}, {month, year}) => {
            // Reshape the thin 202 job into the shared ExportJob the toggle stores consume; the real
            // entity/file fields arrive later via the `export:status_changed` socket event.
            const exportJob: ExportJob = {
                id: job.id,
                entity: 'activity_report_merged',
                status: job.status,
                format: job.format ?? 'csv',
                requestedAt: dayjs().toISOString(),
                downloadUrl: job.downloadUrl,
                fileName: job.fileName
            }
            const period = formatMonthPeriodLabel(`${String(month).padStart(2, '0')}-${year}`)
            // Light up the toggle dot immediately (orange) and give the popover a report-specific
            // title, then announce the pending job so it surfaces on the header toggle right away.
            // The title override survives the pending → ready/failed transition the socket drives.
            useExportsIndicatorStore.getState().track(exportJob.id, exportJob.status)
            useExportAnnouncementStore.getState().registerTitleOverride(exportJob.id, {
                ready: t('dsp_flux_export:merge_announce_ready_title', {period}),
                failed: t('dsp_flux_export:merge_announce_failed_title', {period})
            })
            useExportAnnouncementStore.getState().announce(exportJob)
        },
        onError: error => toast.error(t(resolveActivityReportErrorLabel(error.response?.data?.code)))
    })

    const handleExport = () => {
        // No duplicate submissions while a request is in flight.
        if (!selectedMonth || exportMutation.isPending) return
        exportMutation.mutate(periodToMonthYear(selectedMonth))
    }

    return (
        <ExportCardShell
            icon={
                <StyledIconChip $tone="neutral">
                    <LayersThree01Icon size={20} />
                </StyledIconChip>
            }
            title={t('dsp_flux_export:merge_card_title')}
        >
            <StyledCardFooter>
                <StyledSelectWrapper>
                    <InputSelect
                        value={selectedMonth}
                        optionsEntries={months.map((month): [string, string] => [month, formatMonthPeriodLabel(month)])}
                        onChange={value => setSelectedMonthChoice(value)}
                        disabled={isLoadingMonths || !hasMonths}
                        placeholder={
                            isLoadingMonths ? t('commons:loading') : t('dsp_flux_export:month_select_placeholder')
                        }
                    />
                </StyledSelectWrapper>
                <Button
                    variant="primaryNeutral"
                    onClick={handleExport}
                    disabled={!selectedMonth || exportMutation.isPending}
                >
                    {exportMutation.isPending ? <Spinner size={16} /> : <Download01Icon size={16} />}
                    {t('dsp_flux_export:merge_export_cta')}
                </Button>
            </StyledCardFooter>
        </ExportCardShell>
    )
}
